import { Fragment, useCallback } from 'react';
import { useLocation, useNavigate, useNavigation } from 'react-router';
import { cn } from '@rocco/utils/cn';
import { type ArticleSearchResults } from '../config/article-search';
import {
    DIRECTORY_GUTTER_X,
    EditorialArticleGrid,
} from './EditorialArticleGrid';
import { EditorialFeatureRow } from './EditorialFeatureRow';
import { EditorialViewMore } from './EditorialViewMore';

/** One rhythm of the grid, then the feature row that closes it. */
const RHYTHM_CARDS = 10;
const RUN_CARDS = RHYTHM_CARDS + 1;

const toRuns = <T,>(items: T[], size: number): T[][] => {
    const runs: T[][] = [];
    for (let index = 0; index < items.length; index += size) {
        runs.push(items.slice(index, index + size));
    }
    return runs;
};

interface EditorialSearchResultsProps {
    results: ArticleSearchResults;
    className?: string;
}

/**
 * What the directory lists when a term has been committed in its search field:
 * the articles matching it, in the category pages' own rhythm, with the count
 * over them and the shared View More under.
 *
 * Takes the place of the category sections rather than sitting above them —
 * a reader who has searched is looking for one thing, and the wall of every
 * category under their results would say the search had not narrowed anything.
 */
export const EditorialSearchResults = ({
    results,
    className,
}: EditorialSearchResultsProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();

    // The list is whatever the loader served. Paging is a navigation that
    // writes `?page=N`, and the loader answers it with pages 1..N — so there
    // is no accumulated client state to keep, and no way for the list to
    // disagree with the address bar.
    const { articles, page, hasMore } = results;

    const isLoading =
        navigation.state === 'loading' &&
        navigation.location?.pathname === location.pathname;

    // Paging replaces rather than pushes, as the category listing does: the
    // reader who has taken Load More four times wants Back to leave the
    // listing, not to walk back through four pages of it. Replacing still
    // leaves `?page=N` in the entry, which is what restores the full list
    // when they return from an article.
    const loadMore = useCallback(() => {
        if (isLoading || !hasMore) return;
        const params = new URLSearchParams(location.search);
        params.set('page', String(page + 1));
        navigate(`${location.pathname}?${params.toString()}`, {
            replace: true,
            preventScrollReset: true,
        });
    }, [
        isLoading,
        hasMore,
        page,
        location.pathname,
        location.search,
        navigate,
    ]);

    const clearSearch = () =>
        navigate('/articles', { preventScrollReset: true });

    return (
        <section
            aria-label={`Articles matching ${results.term}`}
            className={className}
            data-testid="editorial-search-results"
        >
            <div
                className={cn(
                    'flex flex-wrap items-baseline justify-between gap-x-8 gap-y-2',
                    DIRECTORY_GUTTER_X,
                )}
            >
                <p className="m-0 font-gestura text-[2rem] leading-[1.1] tracking-[-0.025em] text-[#141414]">
                    {results.unavailable
                        ? 'Search is unavailable just now'
                        : results.totalCount === 0
                          ? `Nothing matches “${results.term}”`
                          : `${results.totalCount.toLocaleString('en-NZ')} ${
                                results.totalCount === 1
                                    ? 'article'
                                    : 'articles'
                            } for “${results.term}”`}
                </p>
                <button
                    type="button"
                    onClick={clearSearch}
                    className="cursor-pointer border-0 bg-transparent p-0 font-sans text-[1rem] leading-[1.5] text-[#575757] underline underline-offset-4 hover:text-[#141414]"
                >
                    Clear search
                </button>
            </div>

            {articles.length > 0 ? (
                <div className="mt-[3rem]">
                    {toRuns(articles, RUN_CARDS).map((run, runIndex) => {
                        const feature = run[RHYTHM_CARDS];
                        return (
                            <Fragment key={run[0]?.id ?? runIndex}>
                                <EditorialArticleGrid
                                    articles={run.slice(0, RHYTHM_CARDS)}
                                    hideStandfirst
                                    graded
                                    // The results open under the rail, so the
                                    // first two rows load with the page, as
                                    // the browse grids' do.
                                    eagerCount={runIndex === 0 ? 6 : 0}
                                    className={
                                        runIndex === 0 ? undefined : 'mt-[5rem]'
                                    }
                                />
                                {feature ? (
                                    <EditorialFeatureRow
                                        article={feature}
                                        className="mt-[5rem]"
                                    />
                                ) : null}
                            </Fragment>
                        );
                    })}
                    <EditorialViewMore
                        hasMore={hasMore}
                        isLoading={isLoading}
                        onLoadMore={loadMore}
                        nextPage={page + 1}
                        className="mt-[4.9375rem]"
                    />
                </div>
            ) : (
                <p
                    className={cn(
                        'mt-[0.75rem] mb-0 text-[1rem] leading-[1.5] text-[#575757]',
                        DIRECTORY_GUTTER_X,
                    )}
                >
                    {results.unavailable
                        ? 'Please try again shortly.'
                        : 'Try another word, or browse the categories above.'}
                </p>
            )}
        </section>
    );
};
